import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Pencil,
} from "lucide-react";
import {
  http,
  rupiah,
  apiError,
} from "@/lib/api";
import AdminShell, {
  PageHead,
  useAdmin,
} from "@/components/AdminShell";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
import MasterDataTransfer from "@/components/MasterDataTransfer";

function ModifiersInner() {
  const admin = useAdmin();
  const isOwner = admin?.user?.role === "owner";

  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [open, setOpen] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [editOption, setEditOption] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [dragGroupId, setDragGroupId] = useState(null);
  const [dragOption, setDragOption] = useState(null);

  const [form, setForm] = useState({
    name: "",
    min_select: 0,
    max_select: 1,
  });

  const [opt, setOpt] = useState({});

  async function load() {
    try {
      const [g, p, inv] = await Promise.all([
        http.get("/admin/modifiers"),
        http.get("/admin/products"),
        http.get("/admin/inventory"),
      ]);

      setGroups(g.data);
      setProducts(p.data);
      setInventory(inv.data || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function addGroup() {
    if (!isOwner) {
      toast.error(
        "Hanya pemilik yang dapat membuat Modifier / Topping."
      );
      return;
    }

    const groupName = toTitleCase(form.name.trim());

    if (!groupName) {
      toast.error("Nama grup wajib diisi.");
      return;
    }

    try {
      await http.post("/admin/modifiers", {
        ...form,
        name: groupName,
      });

      setForm({
        name: "",
        min_select: 0,
        max_select: 1,
      });

      toast.success("Grup dibuat");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function askDeleteGroup(group) {
    if (!isOwner) {
      toast.error(
        "Hanya pemilik yang dapat menghapus Modifier / Topping."
      );
      return;
    }

    setDeleteTarget({
      type: "group",
      id: group.id,
      name: group.name,
    });
  }

  async function addOpt(gid) {
    if (!isOwner) {
      toast.error(
        "Hanya pemilik yang dapat menambahkan opsi Modifier / Topping."
      );
      return;
    }

    const o = opt[gid];
    const optionName = toTitleCase(o?.name?.trim());

    if (!optionName) {
      toast.error("Nama opsi wajib diisi.");
      return;
    }

    if (!o.inventory_id) {
      toast.error("Pilih bahan stok terlebih dahulu.");
      return;
    }

    if (!(Number(o.stock_qty || 0) > 0)) {
      toast.error("Pemakaian stok wajib lebih dari 0.");
      return;
    }

    try {
      await http.post(
        `/admin/modifiers/${gid}/options`,
        {
          name: optionName,
          price_delta:
            parseInt(o.price, 10) || 0,
          stock_usage: {
            inventory_id: o.inventory_id,
            qty: Number(o.stock_qty || 0),
          },
        }
      );

      setOpt({
        ...opt,
        [gid]: {},
      });

      toast.success("Opsi ditambahkan");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function saveOptionStock(gid, option) {
    if (!isOwner) return;

    const key = `stock:${option.id}`;
    const draft = opt[key] || {};

    const inventoryId =
      draft.inventory_id !== undefined
        ? draft.inventory_id
        : option.stock_usage?.inventory_id || "";

    const qty =
      draft.stock_qty !== undefined
        ? Number(draft.stock_qty || 0)
        : Number(option.stock_usage?.qty || 0);

    if (!inventoryId) {
      toast.error("Pilih bahan stok terlebih dahulu.");
      return;
    }

    if (!(qty > 0)) {
      toast.error("Pemakaian stok wajib lebih dari 0.");
      return;
    }

    try {
      await http.patch(
        `/admin/modifiers/${gid}/options/${option.id}`,
        {
          stock_usage: {
            inventory_id: inventoryId,
            qty,
          },
        },
      );

      toast.success("Penggunaan stok disimpan");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function askDeleteOption(gid, option) {
    if (!isOwner) {
      toast.error(
        "Hanya pemilik yang dapat menghapus opsi Modifier / Topping."
      );
      return;
    }

    setDeleteTarget({
      type: "option",
      gid,
      id: option.id,
      name: option.name,
    });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;

    setDeleteBusy(true);

    try {
      if (deleteTarget.type === "group") {
        await http.delete(`/admin/modifiers/${deleteTarget.id}`);
        toast.success("Grup dihapus");
      } else {
        await http.delete(
          `/admin/modifiers/${deleteTarget.gid}/options/${deleteTarget.id}`,
        );
        toast.success("Opsi dihapus");
      }

      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  async function toggleProduct(
    gid,
    pid,
    current
  ) {
    if (!isOwner) {
      toast.error(
        "Hanya pemilik yang dapat mengatur Modifier pada produk."
      );
      return;
    }

    const next = current.includes(pid)
      ? current.filter((x) => x !== pid)
      : [...current, pid];

    try {
      await http.put(
        `/admin/modifiers/${gid}/products`,
        {
          product_ids: next,
        }
      );

      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function toTitleCase(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/(^|\s)\S/g, (char) => char.toUpperCase());
  }

  async function saveGroupEdit() {
    if (!editGroup) return;

    const cleanName = toTitleCase(
      String(editGroup.name || "").trim(),
    );
    const minSelect = Math.max(0, Number(editGroup.min_select || 0));
    const maxSelect = Math.max(1, Number(editGroup.max_select || 1));

    if (!cleanName) {
      toast.error("Nama grup wajib diisi.");
      return;
    }

    if (minSelect > maxSelect) {
      toast.error("Min pilih tidak boleh lebih besar dari Maks pilih.");
      return;
    }

    setEditBusy(true);

    try {
      await http.patch(`/admin/modifiers/${editGroup.id}`, {
        name: cleanName,
        min_select: minSelect,
        max_select: maxSelect,
      });

      toast.success("Grup diperbarui");
      setEditGroup(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEditBusy(false);
    }
  }

  async function saveOptionEdit() {
    if (!editOption) return;

    const cleanName = toTitleCase(
      String(editOption.name || "").trim(),
    );

    if (!cleanName) {
      toast.error("Nama opsi wajib diisi.");
      return;
    }

    if (!editOption.stock_usage?.inventory_id) {
      toast.error("Pilih bahan stok terlebih dahulu.");
      return;
    }

    if (!(Number(editOption.stock_usage?.qty || 0) > 0)) {
      toast.error("Pemakaian stok wajib lebih dari 0.");
      return;
    }

    setEditBusy(true);

    try {
      await http.patch(
        `/admin/modifiers/${editOption.gid}/options/${editOption.id}`,
        {
          name: cleanName,
          price_delta: parseInt(editOption.price_delta, 10) || 0,
          stock_usage: {
            inventory_id:
              editOption.stock_usage.inventory_id,
            qty: Number(
              editOption.stock_usage.qty || 0,
            ),
          },
        },
      );

      toast.success("Opsi diperbarui");
      setEditOption(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEditBusy(false);
    }
  }

  function moveItem(list, fromId, toId) {
    if (!fromId || !toId || fromId === toId) return list;

    const fromIndex = list.findIndex((item) => item.id === fromId);
    const toIndex = list.findIndex((item) => item.id === toId);

    if (fromIndex < 0 || toIndex < 0) return list;

    const next = [...list];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  }

  async function reorderGroups(fromId, toId) {
    if (!isOwner || !fromId || !toId || fromId === toId) {
      setDragGroupId(null);
      return;
    }

    const previous = groups;
    const next = moveItem(groups, fromId, toId);

    setGroups(next);

    try {
      await http.put("/admin/modifiers/reorder", {
        group_ids: next.map((group) => group.id),
      });
    } catch (e) {
      setGroups(previous);
      toast.error(apiError(e));
    } finally {
      setDragGroupId(null);
    }
  }

  async function reorderOptions(gid, fromId, toId) {
    if (!isOwner || !gid || !fromId || !toId || fromId === toId) {
      setDragOption(null);
      return;
    }

    const group = groups.find((item) => item.id === gid);
    if (!group) {
      setDragOption(null);
      return;
    }

    const previous = groups;
    const nextOptions = moveItem(group.options || [], fromId, toId);

    setGroups((current) =>
      current.map((item) =>
        item.id === gid
          ? {
              ...item,
              options: nextOptions,
            }
          : item,
      ),
    );

    try {
      await http.put(`/admin/modifiers/${gid}/options/reorder`, {
        option_ids: nextOptions.map((option) => option.id),
      });
    } catch (e) {
      setGroups(previous);
      toast.error(apiError(e));
    } finally {
      setDragOption(null);
    }
  }

  return (
    <>
      <PageHead
        eyebrow="Katalog"
        title="Modifier / Topping"
        desc={
          isOwner
            ? "Buat opsi tambahan seperti topping & level pedas."
            : "Lihat Modifier / Topping yang tersedia."
        }
      >
        {isOwner && (
          <div className="flex flex-wrap items-center gap-2">
            <MasterDataTransfer
              kind="modifiers"
              label="Modifier"
              onImported={load}
            />
          </div>
        )}
      </PageHead>

      {isOwner && (
        <div className="mb-5 grid grid-cols-2 gap-2 rounded-3xl border border-border bg-card p-4 sm:grid-cols-4">
          <input
            value={form.name}
            onChange={(e) =>
              setForm({
                ...form,
                name: toTitleCase(e.target.value),
              })
            }
            placeholder="Nama grup"
            className="col-span-2 rounded-xl border border-border bg-muted px-3 py-2.5 text-sm outline-none"
            data-testid="modifier-name-input"
          />

          <label className="flex flex-col gap-1 text-[11px] font-bold text-muted-foreground">
            Min pilih

            <input
              type="number"
              value={form.min_select}
              onChange={(e) =>
                setForm({
                  ...form,
                  min_select:
                    parseInt(
                      e.target.value,
                      10
                    ) || 0,
                })
              }
              className="rounded-xl border border-border bg-muted px-3 py-2 text-sm outline-none"
              title="Jumlah opsi wajib dipilih (0 = tidak wajib)"
            />
          </label>

          <label className="flex flex-col gap-1 text-[11px] font-bold text-muted-foreground">
            Maks pilih

            <input
              type="number"
              value={form.max_select}
              onChange={(e) =>
                setForm({
                  ...form,
                  max_select:
                    parseInt(
                      e.target.value,
                      10
                    ) || 1,
                })
              }
              className="rounded-xl border border-border bg-muted px-3 py-2 text-sm outline-none"
              title="Batas maksimal opsi yang bisa dipilih"
            />
          </label>

          <button
            data-testid="add-modifier-btn"
            onClick={addGroup}
            className="col-span-2 flex items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground sm:col-span-4"
          >
            <Plus size={16} />
            Buat Grup
          </button>

          <p className="col-span-2 text-[11px] text-muted-foreground sm:col-span-4">
            💡 <b>Min pilih</b> = jumlah wajib dipilih pelanggan
            (0 = opsional). <b>Maks pilih</b> = batas maksimal opsi
            yang boleh dipilih.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {groups.map((g) => (
          <div
            key={g.id}
            onDragOver={(e) => {
              if (isOwner && dragGroupId) {
                e.preventDefault();
                e.currentTarget.classList.add("ring-2", "ring-primary/30");
              }
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove("ring-2", "ring-primary/30");
            }}
            onDrop={(e) => {
              if (!dragGroupId) return;
              e.preventDefault();
              e.currentTarget.classList.remove("ring-2", "ring-primary/30");
              reorderGroups(dragGroupId, g.id);
            }}
            className={`rounded-3xl border bg-card p-4 transition-all ${
              dragGroupId === g.id
                ? "border-primary/70 opacity-70"
                : "border-border"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex min-w-0 items-start gap-2">
                {isOwner && (
                  <button
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      setDragGroupId(g.id);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragEnd={() => setDragGroupId(null)}
                    className="grid h-11 w-11 flex-none cursor-grab place-items-center rounded-xl border border-border bg-muted text-lg font-black tracking-[-3px] text-muted-foreground transition hover:border-primary/50 hover:bg-primary/10 hover:text-primary active:cursor-grabbing active:scale-95"
                    title="Tarik untuk mengubah urutan grup"
                    aria-label="Tarik untuk mengubah urutan grup"
                  >
                    ⋮⋮
                  </button>
                )}

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <b className="truncate font-heading text-base text-card-foreground">
                      {g.name}
                    </b>

                    {isOwner && (
                      <button
                        type="button"
                        onClick={() =>
                          setEditGroup({
                            id: g.id,
                            name: g.name,
                            min_select: g.min_select,
                            max_select: g.max_select,
                          })
                        }
                        className="text-muted-foreground transition hover:text-primary"
                        title="Edit grup"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    Min {g.min_select} · Max {g.max_select} ·{" "}
                    {(g.options || []).length} opsi
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setOpen(
                      open === g.id
                        ? null
                        : g.id
                    )
                  }
                  className="grid h-9 w-9 place-items-center rounded-xl bg-muted"
                  title="Lihat detail"
                >
                  {open === g.id ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>

                {isOwner && (
                  <button
                    data-testid={`del-modifier-${g.id}`}
                    onClick={() => askDeleteGroup(g)}
                    className="grid h-9 w-9 place-items-center rounded-xl bg-destructive/10 text-destructive"
                    title="Hapus grup"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>

            {open === g.id && (
              <div className="mt-4 space-y-3 border-t border-border pt-4">
                <div className="space-y-2">
                  {(g.options || []).map((o) => {
                    const key = `stock:${o.id}`;
                    const draft = opt[key] || {};

                    const inventoryId =
                      draft.inventory_id !== undefined
                        ? draft.inventory_id
                        : o.stock_usage?.inventory_id || "";

                    const stockQty =
                      draft.stock_qty !== undefined
                        ? draft.stock_qty
                        : o.stock_usage?.qty ?? "";

                    const selectedInventory = inventory.find(
                      (item) => item.id === inventoryId,
                    );

                    return (
                      <div
                        key={o.id}
                        onDragOver={(e) => {
                          if (
                            isOwner &&
                            dragOption?.gid === g.id
                          ) {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.add(
                              "ring-2",
                              "ring-primary/30",
                            );
                          }
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.classList.remove(
                            "ring-2",
                            "ring-primary/30",
                          );
                        }}
                        onDrop={(e) => {
                          if (dragOption?.gid !== g.id) return;
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove(
                            "ring-2",
                            "ring-primary/30",
                          );
                          reorderOptions(
                            g.id,
                            dragOption.oid,
                            o.id,
                          );
                        }}
                        className={`rounded-xl bg-muted px-3 py-2 text-sm transition-all ${
                          dragOption?.oid === o.id
                            ? "ring-2 ring-primary/50 opacity-70"
                            : ""
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-2">
                            {isOwner && (
                              <button
                                type="button"
                                draggable
                                onDragStart={(e) => {
                                  e.stopPropagation();
                                  setDragOption({
                                    gid: g.id,
                                    oid: o.id,
                                  });
                                  e.dataTransfer.effectAllowed = "move";
                                }}
                                onDragEnd={(e) => {
                                  e.stopPropagation();
                                  setDragOption(null);
                                }}
                                className="grid h-9 w-9 flex-none cursor-grab place-items-center rounded-lg border border-border bg-background text-base font-black tracking-[-3px] text-muted-foreground transition hover:border-primary/50 hover:bg-primary/10 hover:text-primary active:cursor-grabbing active:scale-95"
                                title="Tarik untuk mengubah urutan opsi"
                                aria-label="Tarik untuk mengubah urutan opsi"
                              >
                                ⋮⋮
                              </button>
                            )}

                            <span className="truncate text-card-foreground">
                              {o.name}{" "}
                              <span className="text-muted-foreground">
                                ·{" "}
                                {o.price_delta > 0
                                  ? "+" + rupiah(o.price_delta)
                                  : "Gratis"}
                              </span>
                            </span>

                            {isOwner && (
                              <button
                                type="button"
                                onClick={() =>
                                  setEditOption({
                                    gid: g.id,
                                    id: o.id,
                                    name: o.name,
                                    price_delta:
                                      o.price_delta || 0,
                                    stock_usage:
                                      o.stock_usage || {},
                                  })
                                }
                                className="flex-none text-muted-foreground transition hover:text-primary"
                                title="Edit opsi"
                              >
                                <Pencil size={13} />
                              </button>
                            )}
                          </div>

                          {isOwner && (
                            <button
                              onClick={() =>
                                askDeleteOption(g.id, o)
                              }
                              className="flex-none text-destructive"
                              title="Hapus opsi"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>

                        <div className="mt-2 rounded-lg border border-border bg-background/60 p-2">
                          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                            Penggunaan Stok
                          </div>

                          {isOwner ? (
                            <div className="grid grid-cols-[minmax(0,1fr)_100px_auto] gap-2">
                              <select
                                value={inventoryId}
                                onChange={(e) =>
                                  setOpt({
                                    ...opt,
                                    [key]: {
                                      ...draft,
                                      inventory_id: e.target.value,
                                    },
                                  })
                                }
                                className="rounded-lg border border-border bg-muted px-2 py-1.5 text-xs outline-none"
                              >
                                <option value="" disabled>
                                  Pilih bahan stok
                                </option>

                                {inventory.map((item) => (
                                  <option
                                    key={item.id}
                                    value={item.id}
                                  >
                                    {item.name} · {item.stock} {item.unit}
                                  </option>
                                ))}
                              </select>

                              <div className="relative">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.001"
                                  value={stockQty}
                                  disabled={!inventoryId}
                                  onChange={(e) =>
                                    setOpt({
                                      ...opt,
                                      [key]: {
                                        ...draft,
                                        stock_qty: e.target.value,
                                      },
                                    })
                                  }
                                  placeholder="Qty wajib"
                                  className="w-full rounded-lg border border-border bg-muted px-2 py-1.5 pr-9 text-xs outline-none disabled:opacity-50"
                                />

                                {selectedInventory && (
                                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground">
                                    {selectedInventory.unit}
                                  </span>
                                )}
                              </div>

                              <button
                                type="button"
                                onClick={() =>
                                  saveOptionStock(g.id, o)
                                }
                                className="rounded-lg bg-primary px-3 text-xs font-bold text-primary-foreground"
                              >
                                Simpan
                              </button>
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground">
                              {o.stock_usage?.inventory_name
                                ? `${o.stock_usage.inventory_name} · ${o.stock_usage.qty} ${o.stock_usage.unit || ""}`
                                : "Belum ada penggunaan stok"}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {g.options.length === 0 && (
                    <div className="rounded-xl bg-muted px-3 py-3 text-sm text-muted-foreground">
                      Belum ada opsi.
                    </div>
                  )}
                </div>

                {isOwner && (
                  <div className="rounded-2xl border border-border bg-background/40 p-3">
                    <div className="mb-2 text-xs font-bold text-muted-foreground">
                      Tambah Opsi / Topping
                    </div>

                    <div className="grid gap-2 sm:grid-cols-2">
                      <input
                        value={opt[g.id]?.name || ""}
                        onChange={(e) =>
                          setOpt({
                            ...opt,
                            [g.id]: {
                              ...opt[g.id],
                              name: toTitleCase(e.target.value),
                            },
                          })
                        }
                        placeholder="Nama opsi"
                        className="rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none"
                      />

                      <input
                        type="number"
                        value={opt[g.id]?.price || ""}
                        onChange={(e) =>
                          setOpt({
                            ...opt,
                            [g.id]: {
                              ...opt[g.id],
                              price: e.target.value,
                            },
                          })
                        }
                        placeholder="+Rp"
                        className="rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none"
                      />

                      <select
                        value={opt[g.id]?.inventory_id || ""}
                        onChange={(e) =>
                          setOpt({
                            ...opt,
                            [g.id]: {
                              ...opt[g.id],
                              inventory_id: e.target.value,
                            },
                          })
                        }
                        className="rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none"
                      >
                        <option value="" disabled>
                          Pilih bahan stok
                        </option>

                        {inventory.map((item) => (
                          <option
                            key={item.id}
                            value={item.id}
                          >
                            {item.name} · {item.stock} {item.unit}
                          </option>
                        ))}
                      </select>

                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={opt[g.id]?.stock_qty || ""}
                        disabled={!opt[g.id]?.inventory_id}
                        onChange={(e) =>
                          setOpt({
                            ...opt,
                            [g.id]: {
                              ...opt[g.id],
                              stock_qty: e.target.value,
                            },
                          })
                        }
                        placeholder="Qty wajib"
                        className="rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none disabled:opacity-50"
                      />
                    </div>

                    <button
                      onClick={() => addOpt(g.id)}
                      className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground"
                    >
                      <Plus size={16} />
                      Tambah Opsi
                    </button>
                  </div>
                )}

                <div>
                  <div className="mb-2 text-xs font-bold text-muted-foreground">
                    Terapkan ke produk:
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {products.map((p) => {
                      const on =
                        g.product_ids.includes(p.id);

                      return (
                        <button
                          key={p.id}
                          onClick={() =>
                            toggleProduct(
                              g.id,
                              p.id,
                              g.product_ids
                            )
                          }
                          disabled={!isOwner}
                          title={
                            isOwner
                              ? "Ubah penerapan modifier"
                              : "Hanya pemilik yang dapat mengubah penerapan modifier"
                          }
                          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                            on
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          } ${
                            isOwner
                              ? ""
                              : "cursor-not-allowed opacity-70"
                          }`}
                        >
                          {p.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {groups.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Belum ada Modifier / Topping.
          </div>
        )}
      </div>
      {editGroup && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/65 backdrop-blur-sm"
            onClick={() => {
              if (!editBusy) setEditGroup(null);
            }}
          />

          <div className="relative w-full max-w-sm rounded-3xl border border-border bg-card p-5 shadow-2xl">
            <h2 className="font-heading text-xl font-extrabold text-card-foreground">
              Edit Grup Modifier
            </h2>

            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-muted-foreground">
                  Nama Grup
                </span>
                <input
                  autoFocus
                  value={editGroup.name}
                  onChange={(e) =>
                    setEditGroup({
                      ...editGroup,
                      name: toTitleCase(e.target.value),
                    })
                  }
                  className="w-full rounded-xl border border-border bg-muted px-3 py-2.5 text-sm outline-none"
                />
              </label>

              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-muted-foreground">
                    Min pilih
                  </span>
                  <input
                    type="number"
                    min="0"
                    value={editGroup.min_select}
                    onChange={(e) =>
                      setEditGroup({
                        ...editGroup,
                        min_select: e.target.value,
                      })
                    }
                    className="w-full rounded-xl border border-border bg-muted px-3 py-2.5 text-sm outline-none"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-xs font-bold text-muted-foreground">
                    Maks pilih
                  </span>
                  <input
                    type="number"
                    min="1"
                    value={editGroup.max_select}
                    onChange={(e) =>
                      setEditGroup({
                        ...editGroup,
                        max_select: e.target.value,
                      })
                    }
                    className="w-full rounded-xl border border-border bg-muted px-3 py-2.5 text-sm outline-none"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={editBusy}
                  onClick={() => setEditGroup(null)}
                  className="rounded-xl bg-muted py-3 text-sm font-bold"
                >
                  Batal
                </button>

                <button
                  type="button"
                  disabled={editBusy}
                  onClick={saveGroupEdit}
                  className="rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground disabled:opacity-60"
                >
                  {editBusy ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editOption && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/65 backdrop-blur-sm"
            onClick={() => {
              if (!editBusy) setEditOption(null);
            }}
          />

          <div className="relative w-full max-w-sm rounded-3xl border border-border bg-card p-5 shadow-2xl">
            <h2 className="font-heading text-xl font-extrabold text-card-foreground">
              Edit Opsi / Topping
            </h2>

            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-muted-foreground">
                  Nama Opsi
                </span>
                <input
                  autoFocus
                  value={editOption.name}
                  onChange={(e) =>
                    setEditOption({
                      ...editOption,
                      name: toTitleCase(e.target.value),
                    })
                  }
                  className="w-full rounded-xl border border-border bg-muted px-3 py-2.5 text-sm outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-bold text-muted-foreground">
                  Tambahan Harga
                </span>
                <input
                  type="number"
                  value={editOption.price_delta}
                  onChange={(e) =>
                    setEditOption({
                      ...editOption,
                      price_delta: e.target.value,
                    })
                  }
                  className="w-full rounded-xl border border-border bg-muted px-3 py-2.5 text-sm outline-none"
                />
              </label>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={editBusy}
                  onClick={() => setEditOption(null)}
                  className="rounded-xl bg-muted py-3 text-sm font-bold"
                >
                  Batal
                </button>

                <button
                  type="button"
                  disabled={editBusy}
                  onClick={saveOptionEdit}
                  className="rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground disabled:opacity-60"
                >
                  {editBusy ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        title={deleteTarget?.type === "group" ? "Hapus Grup Modifier?" : "Hapus Opsi / Topping?"}
        message={
          deleteTarget?.type === "group"
            ? "Grup modifier akan dihapus dan dilepas dari produk yang menggunakannya."
            : "Opsi topping akan dihapus dari grup modifier."
        }
        itemName={deleteTarget?.name || ""}
        busy={deleteBusy}
        onCancel={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

    </>
  );
}

export default function Modifiers() {
  return (
    <AdminShell>
      <ModifiersInner />
    </AdminShell>
  );
}